import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import { useEffect, useRef, useState } from "react";
import { hapticTap } from "../helpers/haptics";
import { menuColors } from "../theme/menuTheme";
import OptionButton from "./OptionButton";

const LENGTH_PRESETS = [10, 20, 50] as const;

const isPresetLength = (value: number) =>
  LENGTH_PRESETS.includes(value as (typeof LENGTH_PRESETS)[number]);

const LengthOptions = ({
  value,
  max,
  onChange,
  ariaLabel = "Length",
  customAriaLabel = "Custom length"
}: {
  value: number;
  max: number;
  onChange: (value: number) => void;
  ariaLabel?: string;
  customAriaLabel?: string;
}) => {
  const isAll = value === 0;
  const [customActive, setCustomActive] = useState(!isAll && !isPresetLength(value));
  const [draft, setDraft] = useState(!isAll && !isPresetLength(value) ? String(value) : "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value === 0 || isPresetLength(value)) {
      setCustomActive(false);
      return;
    }
    setCustomActive(true);
    setDraft(String(value));
  }, [value]);

  const selectPreset = (next: number) => {
    setCustomActive(false);
    setDraft("");
    onChange(next);
  };

  const selectCustom = () => {
    setCustomActive(true);
    setDraft((current) => current || (value > 0 && !isPresetLength(value) ? String(value) : ""));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const commitCustom = (raw: string) => {
    setDraft(raw);
    setCustomActive(true);
    if (raw.trim() === "") {
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      return;
    }
    const clamped = Math.max(1, max > 0 ? Math.min(max, parsed) : parsed);
    if (clamped === 0 || isPresetLength(clamped)) {
      setCustomActive(false);
      setDraft("");
      onChange(clamped === 0 ? 0 : clamped);
      return;
    }
    onChange(clamped);
  };

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1
      }}>
      {LENGTH_PRESETS.map((preset) => (
        <OptionButton
          key={preset}
          selected={!customActive && value === preset}
          onClick={() => selectPreset(preset)}>
          {preset}
        </OptionButton>
      ))}
      <OptionButton selected={!customActive && isAll} onClick={() => selectPreset(0)}>
        All
      </OptionButton>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "stretch",
          height: 44,
          borderRadius: "10px",
          overflow: "hidden",
          border: "1px solid",
          borderColor: customActive ? menuColors.accent : menuColors.border,
          backgroundColor: customActive ? menuColors.accent : menuColors.surfaceRaised,
          "&:hover": {
            borderColor: customActive ? menuColors.accent : "#64748b",
            backgroundColor: customActive ? menuColors.accent : "rgba(226, 232, 240, 0.06)"
          },
          "&:focus-within": {
            borderColor: menuColors.accent
          }
        }}>
        <InputBase
          inputRef={inputRef}
          type="text"
          inputMode="numeric"
          value={customActive ? draft : ""}
          placeholder="custom"
          onFocus={selectCustom}
          onChange={(event) => commitCustom(event.target.value.replace(/\D/g, "").slice(0, 3))}
          inputProps={{
            maxLength: 3,
            "aria-label": customAriaLabel
          }}
          sx={{
            width: "calc(6ch + 12px)",
            minWidth: "calc(6ch + 12px)",
            height: 44,
            color: customActive ? "#04342C" : menuColors.text,
            "& input": {
              py: 0,
              px: 1,
              height: 44,
              boxSizing: "border-box",
              fontWeight: 600,
              fontSize: 14,
              fontVariantNumeric: "tabular-nums",
              textAlign: "center"
            },
            "& input::placeholder": {
              color: customActive ? "rgba(4, 52, 44, 0.55)" : menuColors.text,
              opacity: 1,
              fontVariantNumeric: "normal"
            }
          }}
        />
        <Button
          tabIndex={-1}
          aria-pressed={customActive}
          aria-label={customAriaLabel}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            hapticTap();
            selectCustom();
          }}
          sx={{
            minWidth: 32,
            width: 32,
            px: 0,
            borderRadius: 0,
            border: "none",
            borderLeft: "1px solid",
            borderColor: customActive ? "rgba(4, 52, 44, 0.28)" : menuColors.border,
            backgroundColor: customActive ? "rgba(4, 52, 44, 0.22)" : "rgba(0, 0, 0, 0.22)",
            color: customActive ? "#04342C" : menuColors.textMuted,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: customActive ? "rgba(4, 52, 44, 0.3)" : "rgba(0, 0, 0, 0.32)",
              boxShadow: "none"
            }
          }}>
          <CheckIcon sx={{ fontSize: 18, opacity: customActive ? 1 : 0.45 }} />
        </Button>
      </Box>
    </Box>
  );
};

export default LengthOptions;
